import { apiRequest } from '../../../api/client';
import type {
  CreateUploadResponseDto,
  DocumentListResponseDto,
  DocumentResponseDto,
  ImageEvidenceDetailDto,
  ImageEvidenceResponseDto,
  MachineDocument,
} from '../types/document';
import { canonicalContentType, mapMachineDocument } from '../types/document';

export interface DocumentListResult {
  documents: MachineDocument[];
}

export interface UploadFileDescriptor {
  clientFileId: string;
  file: File;
}

export async function listMachineDocuments(
  machineId: string,
  lifecycleStatus: 'ACTIVE' | 'REMOVED' = 'ACTIVE',
): Promise<DocumentListResult> {
  const response = await apiRequest<DocumentListResponseDto>(
    `/v1/machines/${encodeURIComponent(machineId)}/documents?lifecycle_status=${encodeURIComponent(lifecycleStatus)}`,
  );
  return {
    documents: response.documents
      .map(mapMachineDocument)
      .sort((left, right) => {
        const a = left.uploadedAt ?? left.createdAt ?? '';
        const b = right.uploadedAt ?? right.createdAt ?? '';
        return b.localeCompare(a);
      }),
  };
}

export async function getDocument(documentId: string): Promise<MachineDocument> {
  const response = await apiRequest<DocumentResponseDto>(`/v1/documents/${encodeURIComponent(documentId)}`);
  return mapMachineDocument(response.document);
}

export async function getImageEvidence(documentId: string): Promise<ImageEvidenceDetailDto> {
  const response = await apiRequest<ImageEvidenceResponseDto>(
    `/v1/documents/${encodeURIComponent(documentId)}/image-evidence`,
  );
  return response.image_evidence;
}

export async function createUploadRequests(machineId: string, files: UploadFileDescriptor[]): Promise<CreateUploadResponseDto> {
  return apiRequest<CreateUploadResponseDto>('/v1/documents/upload-requests', {
    method: 'POST',
    body: JSON.stringify({
      machine_id: machineId,
      files: files.map(({ clientFileId, file }) => ({
        client_file_id: clientFileId,
        original_filename: file.name,
        content_type: canonicalContentType(file),
        file_size_bytes: file.size,
        last_modified_at: file.lastModified ? new Date(file.lastModified).toISOString() : null,
      })),
    }),
  });
}

export async function cancelDocumentUpload(
  documentId: string,
  reason: 'USER_CANCELLED' | 'USER_REMOVED' | 'USER_RETRY' = 'USER_CANCELLED',
): Promise<MachineDocument> {
  const response = await apiRequest<DocumentResponseDto>(`/v1/documents/${encodeURIComponent(documentId)}/upload/cancel`, {
    method: 'POST',
    body: JSON.stringify({ reason }),
  });
  return mapMachineDocument(response.document);
}


export type DocumentRemovalReason = 'WRONG_DOCUMENT' | 'DUPLICATE_DOCUMENT' | 'OUTDATED_DOCUMENT' | 'OTHER';

export async function removeDocument(
  documentId: string,
  reason: DocumentRemovalReason,
  expectedVersion: number,
): Promise<MachineDocument> {
  const response = await apiRequest<DocumentResponseDto>(`/v1/documents/${encodeURIComponent(documentId)}/remove`, {
    method: 'POST',
    body: JSON.stringify({ reason, expected_version: expectedVersion }),
  });
  return mapMachineDocument(response.document);
}

export async function retryDocumentReconciliation(
  documentId: string,
  expectedEvidenceRevision: number,
): Promise<MachineDocument> {
  const response = await apiRequest<DocumentResponseDto>(`/v1/documents/${encodeURIComponent(documentId)}/reconciliation/retry`, {
    method: 'POST',
    body: JSON.stringify({ expected_evidence_revision: expectedEvidenceRevision }),
  });
  return mapMachineDocument(response.document);
}

export async function reviewDocument(
  documentId: string,
  decision: 'CONFIRM' | 'REJECT',
): Promise<MachineDocument> {
  const response = await apiRequest<DocumentResponseDto>(`/v1/documents/${encodeURIComponent(documentId)}/review-decisions`, {
    method: 'POST',
    body: JSON.stringify({ decision }),
  });
  return mapMachineDocument(response.document);
}
